import { Container } from '@/ui/design-system/Container'
import { Badge } from '@/ui/components'
import { Bell } from '@/ui/icons'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useRef } from 'react'

const NOTIFICATIONS = [
  {
    title: 'Trip invite',
    body: 'Alex invited you to Cascades Loop',
    time: 'now'
  },
  {
    title: 'Crew update',
    body: 'Jordan is heading out, ETA 4:30 PM',
    time: '12m'
  },
  {
    title: 'Arrived at camp',
    body: 'Sam arrived at Ridge Camp',
    time: '1h'
  }
]

export function MobileAppSection() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 })
  const reducedMotion = useReducedMotion()
  const inView = reducedMotion || isInView

  const fadeInLeft = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  return (
    <section ref={sectionRef} className="py-20 lg:py-40 bg-earth-50 overflow-hidden">
      <Container size="app">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <motion.div
            className="flex flex-col gap-6"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeInLeft}
          >
            <div>
              <Badge variant="outline" className="gap-1.5">
                <Bell className="h-3 w-3" />
                Coming soon to iOS &amp; Android
              </Badge>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
              The trip in your pocket
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The SitRepp mobile app is almost here. Get push notifications the moment something happens on your trip: an invite lands, a crew member rolls out, or the first rig pulls into camp.
            </p>
            <ul className="space-y-3">
              {[
                'Know the second you\'re invited to a trip',
                'See when crew members are heading out',
                'Get a ping when someone arrives at camp',
                'Your itinerary, meals, and gear lists on the trail'
              ].map((item, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-start gap-3"
                  initial={reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, delay: 0.3 + (idx * 0.1), ease: [0.6, -0.05, 0.01, 0.99] as const }}
                >
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-adventure-500 flex-shrink-0" />
                  <span className="text-base text-earth-700">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Phone mockup */}
          <div className="flex justify-center lg:justify-end">
            <motion.div
              className="relative w-[280px] sm:w-[300px]"
              initial={reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
              transition={{ duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }}
            >
              {/* Frame */}
              <div className="rounded-[3rem] border-[10px] border-earth-900 bg-earth-800 shadow-2xl overflow-hidden">
                {/* Notch */}
                <div className="relative bg-gradient-to-b from-earth-700 to-earth-800 pt-3">
                  <div className="mx-auto h-6 w-28 rounded-full bg-earth-900" />
                </div>
                {/* Screen */}
                <div className="bg-gradient-to-b from-earth-700 via-earth-800 to-earth-900 px-3 pt-8 pb-16 min-h-[480px]">
                  <p className="text-center text-white/60 text-xs mb-1">Tuesday, June 9</p>
                  <p className="text-center text-white text-4xl font-semibold mb-8">4:18</p>
                  <div className="space-y-3">
                    {NOTIFICATIONS.map((n, idx) => (
                      <motion.div
                        key={idx}
                        className="rounded-2xl bg-white/95 p-3 shadow-lg"
                        initial={reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                        transition={{ duration: 0.45, delay: 0.4 + (idx * 0.25), ease: [0.6, -0.05, 0.01, 0.99] as const }}
                      >
                        <div className="flex items-start gap-2.5">
                          <img
                            src="/favicon-128.png"
                            alt="SitRepp"
                            className="mt-0.5 h-8 w-8 shrink-0 rounded-xl"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <p className="text-xs font-semibold text-earth-900">SitRepp · {n.title}</p>
                              <span className="text-[10px] text-earth-500 shrink-0">{n.time}</span>
                            </div>
                            <p className="text-xs text-earth-700 truncate">{n.body}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Soft glow behind phone */}
              <div className="absolute -inset-6 -z-10 rounded-full bg-adventure-500/10 blur-3xl" aria-hidden />
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  )
}
